import type { Account } from '@limo/database'
import type { LucideIcon } from 'lucide-react'
import {
  Building2,
  MapPin,
  Image,
  Car,
  DollarSign,
  Palette,
  Search,
  CreditCard,
  BadgeCheck,
} from 'lucide-react'

export interface OnboardingStep {
  id: string
  label: string
  description: string
  category: 'business' | 'fleet' | 'website' | 'payments'
  icon: LucideIcon
  /** Path relative to /dashboard (e.g. "/settings/business") */
  href: string
  evaluate: (account: Account, data: OnboardingSupplementaryData) => boolean
}

export interface OnboardingSupplementaryData {
  settingsByAccount: Map<string, Map<string, string>>
  activeVehicleCountByAccount: Map<string, number>
  hasVehiclePricingByAccount: Map<string, boolean>
  hasActiveSubscriptionByAccount: Map<string, boolean>
}

export interface OnboardingResult {
  completedSteps: string[]
  totalSteps: number
  percentage: number
  steps: Array<{ step: OnboardingStep; completed: boolean }>
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'business_profile',
    label: 'Business Profile',
    description: 'Set business email, phone number, and timezone',
    category: 'business',
    icon: Building2,
    href: '/settings/business',
    evaluate: (account) => {
      return !!account.business_email && !!account.business_phone && !!account.timezone
    }
  },
  {
    id: 'business_address',
    label: 'Business Address',
    description: 'Add street address, city, state, and postal code',
    category: 'business',
    icon: MapPin,
    href: '/settings/business',
    evaluate: (account) =>
      !!account.address_line1 && !!account.city && !!account.state && !!account.postal_code,
  },
  {
    id: 'logo',
    label: 'Logo',
    description: 'Upload a company logo',
    category: 'business',
    icon: Image,
    href: '/settings/business',
    evaluate: (account) => !!account.logo_url,
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    description: 'Add at least one active vehicle',
    category: 'fleet',
    icon: Car,
    href: '/vehicles',
    evaluate: (account, data) => {
      const count = data.activeVehicleCountByAccount.get(account.id) ?? 0
      return count > 0
    },
  },
  {
    id: 'vehicle_pricing',
    label: 'Vehicle Pricing',
    description: 'Configure pricing for at least one vehicle',
    category: 'fleet',
    icon: DollarSign,
    href: '/vehicles',
    evaluate: (account, data) =>
      data.hasVehiclePricingByAccount.get(account.id) ?? false,
  },
  {
    id: 'website_branding',
    label: 'Website Branding',
    description: 'Set a primary brand color',
    category: 'website',
    icon: Palette,
    href: '/settings/branding',
    evaluate: (account, data) => {
      const settings = data.settingsByAccount.get(account.id)
      return !!settings?.get('branding_primary_color')
    },
  },
  {
    id: 'seo',
    label: 'SEO',
    description: 'Add meta title and meta description',
    category: 'website',
    icon: Search,
    href: '/settings/seo',
    evaluate: (account, data) => {
      const settings = data.settingsByAccount.get(account.id)
      return !!settings?.get('seo_meta_title') && !!settings?.get('seo_meta_description')
    },
  },
  {
    id: 'payment_setup',
    label: 'Payment Setup',
    description: 'Configure Stripe API keys and enable payments',
    category: 'payments',
    icon: CreditCard,
    href: '/settings/integrations',
    evaluate: (account, data) => {
      const settings = data.settingsByAccount.get(account.id)
      return (
        settings?.get('stripe_enabled') === 'true' &&
        !!settings?.get('stripe_publishable_key') &&
        !!settings?.get('stripe_secret_key')
      )
    },
  },
  {
    id: 'subscription',
    label: 'Subscription',
    description: 'Active or trialing subscription',
    category: 'payments',
    icon: BadgeCheck,
    href: '/settings/billing',
    evaluate: (account, data) =>
      data.hasActiveSubscriptionByAccount.get(account.id) ?? false,
  },
]

export const CATEGORY_LABELS: Record<string, string> = {
  business: 'Business Setup',
  fleet: 'Fleet & Pricing',
  website: 'Website Configuration',
  payments: 'Payments & Billing',
}

export function evaluateOnboarding(
  account: Account,
  data: OnboardingSupplementaryData
): OnboardingResult {
  const steps = ONBOARDING_STEPS.map((step) => ({
    step,
    completed: step.evaluate(account, data),
  }))

  const completedSteps = steps.filter((s) => s.completed).map((s) => s.step.id)
  const totalSteps = ONBOARDING_STEPS.length
  const percentage = Math.round((completedSteps.length / totalSteps) * 100)

  return { completedSteps, totalSteps, percentage, steps }
}
